/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼詳細サイドの SLA ステータスカード。未クローズは起票からの経過営業日 vs 目標（ライブ逼迫）を、クローズ済みは解決日と「解決までの営業日 vs 目標」（SLO 達成=緑 / 未達=赤）を表示する。ヘッダの ? から SLO の目標営業日の説明を開ける。SLA/SLO のロジックは ops-core に委譲する
 * @graph-connects OpsSloExplainer [references] ヘッダの ? から SLO 目標営業日の説明を開く
 * @graph-connects computeOpsSla [calls] 未クローズのライブ SLA（経過・目標・状態）を算出
 * @graph-connects computeOpsSloOutcome [calls] クローズ済みの解決までの営業日と SLO 達成可否を算出
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { computeOpsSla, computeOpsSloOutcome } from '@cortex/ops-core';
import { InfoRow } from './OpsPrimitives.js';
import { OpsSloExplainer } from './OpsSloExplainer.js';
import type { OpsRequest } from '../../../types/ops.js';

/** SLA 経過率 → バーの静的な幅クラス（インラインスタイルを避けるためバケット化）。 @graph-connects none */
const FILL_WIDTH = ['w-[6%]', 'w-1/4', 'w-1/2', 'w-3/4', 'w-full'];

/** 経過率をバーのバケット index に変換する。 @graph-connects none */
function fillBucket(elapsed: number, target: number | null): number {
  if (target === null || target === 0) return elapsed > 0 ? 4 : 0;
  const pct = elapsed / target;
  if (pct <= 0.1) return 0;
  if (pct <= 0.35) return 1;
  if (pct <= 0.6) return 2;
  if (pct < 1) return 3;
  return 4;
}

/** FillBar の props。 */
interface FillBarProps {
  /** 経過（または解決までの）営業日数。 */
  elapsed: number;
  /** 目標営業日数（対象外は null）。 */
  target: number | null;
  /** バーの塗り色（Tailwind クラス）。 */
  color: string;
}

/** 経過バー。target に対する elapsed の割合を色付きで示す。 @graph-connects none */
function FillBar({ elapsed, target, color }: FillBarProps): ReactNode {
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-hover)]">
      <div className={`h-full rounded-full ${color} ${FILL_WIDTH[fillBucket(elapsed, target)]}`} />
    </div>
  );
}

/** OpsSlaCard の props。 */
export interface OpsSlaCardProps {
  /** 表示対象の依頼。 */
  request: OpsRequest;
}

/**
 * 依頼詳細サイドの SLA ステータスカード。未クローズは「起票 → 経過営業日 / 目標」（ライブ逼迫）を、
 * クローズ済みは「起票 → 解決日 → 完了までの営業日 / 目標」（SLO 達成=緑 / 未達=赤）を出す。解決日が
 * 打刻され SLA 対象カテゴリのときだけ「完了」値 + バーを描画する（対象外カテゴリ・legacy の解決日欠落は '—'）。
 *
 * クローズ判定・解決日・SLO 判定はすべて `request`（サーバー確定データ）を single source にする。親の
 * ローカル楽観 state を混ぜると、解決ボタン押下から PATCH レスポンス到達までの間 `isClosed=true` かつ
 * `completedAt=null` になり「解決 —／完了 —」の空白表示に崩れるため、判定源を request 側に一本化する
 * （PATCH 成功で TanStack Query キャッシュが closed + completedAt に差し替わってからクローズ表示へ移る）。
 * @graph-connects none
 */
export function OpsSlaCard({ request }: OpsSlaCardProps): ReactNode {
  const { t } = useTranslation();
  const sla = computeOpsSla(request);
  const liveFillColor =
    sla.state === 'breach'
      ? 'bg-[var(--color-error)]'
      : sla.state === 'warn'
        ? 'bg-[var(--color-warning)]'
        : 'bg-[var(--color-success)]';
  const isClosed = request.state === 'closed';
  const sloOutcome = computeOpsSloOutcome(request);
  const resolvedDate = request.completedAt !== null ? request.completedAt.slice(0, 10) : null;
  const showCompletedSlo = isClosed && resolvedDate !== null && sloOutcome.eligible;
  const completedFillColor = sloOutcome.metTarget
    ? 'bg-[var(--color-success)]'
    : 'bg-[var(--color-error)]';

  return (
    <Card padding="sm">
      <CardHeader title={t('ops.detail.slaStatus')} action={<OpsSloExplainer />} />
      <CardContent className="space-y-2 text-sm">
        <InfoRow label={t('ops.detail.filed')}>{request.submittedAt.slice(0, 10)}</InfoRow>
        {isClosed ? (
          <>
            <InfoRow label={t('ops.detail.resolved')}>{resolvedDate ?? '—'}</InfoRow>
            <InfoRow label={t('ops.detail.completed')}>
              {showCompletedSlo
                ? t('ops.detail.completedValue', {
                    elapsed: sloOutcome.elapsedDays,
                    target: sloOutcome.targetDays,
                  })
                : '—'}
            </InfoRow>
            {showCompletedSlo && (
              <FillBar
                elapsed={sloOutcome.elapsedDays}
                target={sloOutcome.targetDays}
                color={completedFillColor}
              />
            )}
          </>
        ) : (
          <>
            <InfoRow label={t('ops.detail.elapsed')}>
              {sla.targetDays === null
                ? '—'
                : t('ops.detail.elapsedValue', {
                    elapsed: sla.elapsedDays,
                    target: sla.targetDays,
                  })}
            </InfoRow>
            {sla.state !== 'none' && (
              <FillBar elapsed={sla.elapsedDays} target={sla.targetDays} color={liveFillColor} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
